"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { authorizedFetch } from "@/lib/auth/authorized-fetch";
import { MAX_SPEECH_SESSION_MS, STT_LATENCY_TARGET_MS } from "@/lib/prism/stt/constants";
import { appendFinalTranscript, composeSpeechValue } from "@/lib/prism/stt/merge-transcript";
import {
    createMicAudioPipeline,
    DEFAULT_SPEECH_AUDIO_LEVELS,
    type MicAudioPipeline,
} from "@/lib/prism/stt/mic-audio-pipeline";

type SpeechSdkModule = typeof import("microsoft-cognitiveservices-speech-sdk");

export type UsePrismSpeechInputOptions = {
    value: string;
    onValueChange: (v: string) => void;
    disabled?: boolean;
    /** When false, mic is hidden (Speech not configured on server). */
    available?: boolean;
};

export type UsePrismSpeechInputReturn = {
    available: boolean;
    listening: boolean;
    audioLevels: number[];
    error: string | null;
    toggleListening: () => Promise<void>;
    stopListening: () => void;
};

type TokenResponse = {
    success?: boolean;
    token?: string;
    region?: string;
    language?: string;
    error?: string;
};

async function notifySessionEnd(): Promise<void> {
    try {
        await authorizedFetch("/api/prism/stt/token", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ action: "end" }),
        });
    } catch {
        // Best-effort audit only.
    }
}

export function usePrismSpeechInput({
    value,
    onValueChange,
    disabled = false,
    available = true,
}: UsePrismSpeechInputOptions): UsePrismSpeechInputReturn {
    const [listening, setListening] = useState(false);
    const [audioLevels, setAudioLevels] = useState<number[]>(DEFAULT_SPEECH_AUDIO_LEVELS);
    const [error, setError] = useState<string | null>(null);

    const sdkRef = useRef<SpeechSdkModule | null>(null);
    const recognizerRef = useRef<InstanceType<SpeechSdkModule["SpeechRecognizer"]> | null>(null);
    const pipelineRef = useRef<MicAudioPipeline | null>(null);
    const committedRef = useRef("");
    const sessionStartedRef = useRef(false);
    const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const latencyStartRef = useRef<number | null>(null);
    const firstPartialLoggedRef = useRef(false);

    const clearMaxTimer = useCallback(() => {
        if (maxTimerRef.current) {
            clearTimeout(maxTimerRef.current);
            maxTimerRef.current = null;
        }
    }, []);

    const disposePipeline = useCallback(() => {
        const pipeline = pipelineRef.current;
        pipelineRef.current = null;
        if (pipeline) {
            pipeline.stopLevelMonitoring();
            pipeline.dispose();
        }
        setAudioLevels([...DEFAULT_SPEECH_AUDIO_LEVELS]);
    }, []);

    const stopListening = useCallback(() => {
        clearMaxTimer();
        pipelineRef.current?.stopLevelMonitoring();

        const recognizer = recognizerRef.current;
        recognizerRef.current = null;

        const finish = () => {
            disposePipeline();
            if (sessionStartedRef.current) {
                sessionStartedRef.current = false;
                void notifySessionEnd();
            }
            setListening(false);
        };

        if (recognizer) {
            recognizer.stopContinuousRecognitionAsync(
                () => {
                    recognizer.close();
                    finish();
                },
                () => {
                    recognizer.close();
                    finish();
                },
            );
        } else {
            finish();
        }
    }, [clearMaxTimer, disposePipeline]);

    const startListening = useCallback(async () => {
        if (disabled || !available || listening) return;

        setError(null);
        firstPartialLoggedRef.current = false;
        latencyStartRef.current = performance.now();
        committedRef.current = value;

        const tokenRes = await authorizedFetch("/api/prism/stt/token", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: "{}",
        });

        const tokenJson = (await tokenRes.json()) as TokenResponse;
        if (!tokenRes.ok || !tokenJson.token || !tokenJson.region) {
            setError(tokenJson.error ?? "Voice input is unavailable");
            return;
        }

        if (!sdkRef.current) {
            sdkRef.current = await import("microsoft-cognitiveservices-speech-sdk");
        }
        const sdk = sdkRef.current;

        let pipeline: MicAudioPipeline;
        try {
            pipeline = await createMicAudioPipeline(sdk);
        } catch (err) {
            const message =
                err instanceof Error ? err.message : "Microphone permission denied";
            setError(message);
            return;
        }

        pipelineRef.current = pipeline;
        pipeline.startLevelMonitoring(setAudioLevels);

        const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(
            tokenJson.token,
            tokenJson.region,
        );
        speechConfig.speechRecognitionLanguage = tokenJson.language ?? "en-US";
        speechConfig.setProperty(
            sdk.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs,
            "2000",
        );

        const audioConfig = sdk.AudioConfig.fromStreamInput(pipeline.pushStream);
        const recognizer = new sdk.SpeechRecognizer(speechConfig, audioConfig);
        recognizerRef.current = recognizer;
        sessionStartedRef.current = true;

        recognizer.recognizing = (_s, e) => {
            const interim = e.result?.text ?? "";
            if (interim && !firstPartialLoggedRef.current && latencyStartRef.current !== null) {
                firstPartialLoggedRef.current = true;
                const ttfp = Math.round(performance.now() - latencyStartRef.current);
                if (process.env.NODE_ENV !== "production") {
                    console.info(`[prism-stt] time-to-first-partial: ${ttfp}ms (target ${STT_LATENCY_TARGET_MS}ms)`);
                }
            }
            onValueChange(composeSpeechValue(committedRef.current, interim));
        };

        recognizer.recognized = (_s, e) => {
            if (e.result?.reason === sdk.ResultReason.RecognizedSpeech) {
                committedRef.current = appendFinalTranscript(
                    committedRef.current,
                    e.result.text ?? "",
                );
                onValueChange(committedRef.current);
            }
        };

        recognizer.canceled = (_s, e) => {
            if (e.errorDetails) {
                setError(e.errorDetails);
            }
            stopListening();
        };

        recognizer.sessionStopped = () => {
            stopListening();
        };

        recognizer.startContinuousRecognitionAsync(
            () => {
                setListening(true);
                maxTimerRef.current = setTimeout(() => {
                    stopListening();
                }, MAX_SPEECH_SESSION_MS);
            },
            (err) => {
                setError(typeof err === "string" ? err : "Failed to start microphone");
                stopListening();
            },
        );
    }, [available, disabled, listening, onValueChange, stopListening, value]);

    const toggleListening = useCallback(async () => {
        if (listening) {
            stopListening();
            return;
        }
        await startListening();
    }, [listening, startListening, stopListening]);

    useEffect(() => {
        return () => {
            stopListening();
        };
    }, [stopListening]);

    return {
        available: available && !disabled,
        listening,
        audioLevels,
        error,
        toggleListening,
        stopListening,
    };
}
